import { create } from 'zustand';
import type { Tenant } from '@cartiq/shared';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role?: string;
}

const AUTH_HINT_KEY = 'cartiq:isAuthed';
const AUTH_CHANNEL = 'cartiq:auth';

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  tenant: Tenant | null;
  /** A non-sensitive hint that the user was previously authenticated. */
  isAuthedHint: boolean;
  setToken: (token: string | null) => void;
  setSession: (args: { accessToken: string; user: AuthUser; tenant: Tenant }) => void;
  setUser: (user: AuthUser | null) => void;
  setTenant: (tenant: Tenant | null) => void;
  logout: () => void;
}

function readHint(): boolean {
  try {
    return localStorage.getItem(AUTH_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHint(v: boolean) {
  try {
    if (v) localStorage.setItem(AUTH_HINT_KEY, '1');
    else localStorage.removeItem(AUTH_HINT_KEY);
  } catch {
    /* ignore */
  }
}

// ---- Cross-tab session sharing ----
// The access token lives only in memory (never persisted, to limit XSS blast
// radius), so each browser tab starts without one. The refresh cookie is
// single-use and rotated server-side, so if two tabs independently call
// /auth/refresh they race and one ends up presenting a revoked token — which
// logs that tab out. To avoid this we share the live session between tabs over
// a BroadcastChannel: a freshly opened tab asks its peers for the current
// session instead of hitting the network, and any tab that obtains a new token
// (login or refresh) pushes it to the others.

type AuthMessage =
  | { type: 'session'; accessToken: string; user: AuthUser | null; tenant: Tenant | null }
  | { type: 'token'; accessToken: string | null }
  | { type: 'logout' }
  | { type: 'request' };

let channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    channel = new BroadcastChannel(AUTH_CHANNEL);
  } catch {
    channel = null;
  }
  return channel;
}

function broadcast(msg: AuthMessage) {
  try {
    getChannel()?.postMessage(msg);
  } catch {
    /* ignore */
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  tenant: null,
  isAuthedHint: readHint(),
  setToken: (token) => {
    writeHint(!!token);
    set({ accessToken: token, isAuthedHint: !!token });
    broadcast({ type: 'token', accessToken: token });
  },
  setSession: ({ accessToken, user, tenant }) => {
    writeHint(true);
    set({ accessToken, user, tenant, isAuthedHint: true });
    broadcast({ type: 'session', accessToken, user, tenant });
  },
  setUser: (user) => set({ user }),
  setTenant: (tenant) => set({ tenant }),
  logout: () => {
    writeHint(false);
    set({ accessToken: null, user: null, tenant: null, isAuthedHint: false });
    broadcast({ type: 'logout' });
  },
}));

// Apply updates received from other tabs WITHOUT re-broadcasting (we call
// setState directly rather than the broadcasting actions, to avoid an echo).
function applyRemote(msg: AuthMessage) {
  switch (msg.type) {
    case 'session':
      writeHint(!!msg.accessToken);
      useAuthStore.setState({
        accessToken: msg.accessToken,
        user: msg.user,
        tenant: msg.tenant,
        isAuthedHint: !!msg.accessToken,
      });
      break;
    case 'token':
      writeHint(!!msg.accessToken);
      useAuthStore.setState({ accessToken: msg.accessToken, isAuthedHint: !!msg.accessToken });
      break;
    case 'logout':
      writeHint(false);
      useAuthStore.setState({
        accessToken: null,
        user: null,
        tenant: null,
        isAuthedHint: false,
      });
      break;
    case 'request': {
      // A peer is bootstrapping — hand it our live session if we have one.
      const { accessToken, user, tenant } = useAuthStore.getState();
      if (accessToken) broadcast({ type: 'session', accessToken, user, tenant });
      break;
    }
  }
}

if (typeof window !== 'undefined') {
  const ch = getChannel();
  if (ch) {
    ch.onmessage = (e: MessageEvent<AuthMessage>) => applyRemote(e.data);
    // On boot, if we look authenticated but have no token yet, ask any open tab
    // to share its session so we can skip the network round-trip (and the
    // refresh-token rotation that would race with the other tab).
    if (readHint()) broadcast({ type: 'request' });
  }
}

/** Non-hook accessors for use inside the axios interceptor. */
export const authActions = {
  getToken: () => useAuthStore.getState().accessToken,
  setToken: (t: string | null) => useAuthStore.getState().setToken(t),
  setSession: (args: { accessToken: string; user: AuthUser; tenant: Tenant }) =>
    useAuthStore.getState().setSession(args),
  logout: () => useAuthStore.getState().logout(),
};
